// ============================================
// ORDER PANEL COMPONENT
// Current order with items, totals, and actions
// ============================================

import React, { useState } from 'react';
import { 
  Plus, 
  Minus, 
  Trash2, 
  Pause, 
  X, 
  CreditCard,
  Receipt,
  Users,
  Clock,
  Package,
  Truck,
  UtensilsCrossed,
  MoreVertical,
  Percent,
  RefreshCw,
  Split,
  Merge,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const ORDER_TYPE_CONFIG = {
  dineIn: { label: 'Dine In', icon: UtensilsCrossed, color: 'bg-brand-orange' },
  takeaway: { label: 'Takeaway', icon: Package, color: 'bg-amber-500' },
  delivery: { label: 'Delivery', icon: Truck, color: 'bg-blue-500' },
};

// Format elapsed time
function formatElapsedTime(startTime) {
  if (!startTime) return '';
  const elapsed = Math.floor((Date.now() - new Date(startTime).getTime()) / 60000);
  if (elapsed < 60) return `${elapsed}m`;
  const hours = Math.floor(elapsed / 60);
  const mins = elapsed % 60;
  return `${hours}h ${mins}m`;
}

export function OrderPanel({
  order,
  orderType,
  totals,
  table,
  onUpdateQuantity,
  onRemoveItem,
  onHoldOrder,
  onCancelOrder,
  onPayment,
  formatPrice,
  isManager,
  vatLabel = 'MVA',
}) {
  const [showHoldDialog, setShowHoldDialog] = useState(false);
  const [holdNote, setHoldNote] = useState('');

  const typeConfig = ORDER_TYPE_CONFIG[orderType] || ORDER_TYPE_CONFIG.dineIn;
  const TypeIcon = typeConfig.icon;

  const handleHold = () => {
    onHoldOrder(holdNote);
    setHoldNote('');
    setShowHoldDialog(false);
  };

  // Empty state
  if (!order) {
    return (
      <div 
        data-testid="order-panel-empty"
        className="h-full bg-white rounded-lg shadow-sm flex flex-col items-center justify-center text-gray-400 p-8"
      >
        <UtensilsCrossed className="w-16 h-16 mb-4 opacity-30" />
        <p className="text-lg font-medium mb-2">No Active Order</p>
        <p className="text-sm text-center">
          Select a table or start a quick order
        </p>
      </div>
    );
  }

  return (
    <div data-testid="order-panel" className="h-full bg-white rounded-lg shadow-sm flex flex-col">
      {/* Order Header */}
      <div className="p-3 border-b">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge className={cn('gap-1.5', typeConfig.color)}>
              <TypeIcon className="w-3.5 h-3.5" />
              {typeConfig.label}
            </Badge>
            {table && (
              <Badge variant="outline" className="font-mono">
                {table.id}
              </Badge>
            )}
          </div>
          
          {/* Order Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="gap-2">
                <MessageSquare className="w-4 h-4" />
                Add Note
              </DropdownMenuItem>
              {isManager && (
                <>
                  <DropdownMenuItem className="gap-2">
                    <Percent className="w-4 h-4" />
                    Apply Discount
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Transfer Table
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2">
                <Split className="w-4 h-4" />
                Split Bill
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2">
                <Merge className="w-4 h-4" />
                Merge Orders
              </DropdownMenuItem>
              {isManager && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2 text-red-600">
                    <X className="w-4 h-4" />
                    Void Order
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Order Meta */}
        <div className="flex items-center gap-4 text-sm text-gray-500">
          {order.guests > 0 && (
            <div className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              <span>{order.guests} guests</span>
            </div>
          )}
          {order.startTime && (
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{formatElapsedTime(order.startTime)}</span>
            </div>
          )}
          <div className="text-xs text-gray-400 font-mono">
            #{order.id?.split('-').pop() || '---'}
          </div>
        </div>
      </div>

      {/* Order Items */}
      <ScrollArea className="flex-1">
        <div data-testid="order-items" className="p-3 space-y-2">
          {order.items.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">Add items from the menu</p>
            </div>
          ) : (
            order.items.map((item, index) => (
              <div 
                key={`${item.id}-${index}`}
                data-testid={`order-item-${item.id}`}
                className="group flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {/* Item Details */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-800 leading-tight">
                    {item.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {formatPrice(item.price)} × {item.quantity}
                  </div>
                </div>

                {/* Quantity Controls */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onUpdateQuantity(item.id, -1)}
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <span className="w-8 text-center font-mono font-semibold text-sm">
                    {item.quantity}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onUpdateQuantity(item.id, 1)}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>

                {/* Line Total */}
                <div className="text-right">
                  <div className="font-mono font-semibold text-sm">
                    {formatPrice(item.price * item.quantity)}
                  </div>
                </div>

                {/* Remove Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => onRemoveItem(item.id)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Order Totals */}
      {order.items.length > 0 && (
        <div className="p-4 border-t bg-gray-50/50">
          {/* Subtotal */}
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Subtotal</span>
            <span className="font-mono">{formatPrice(totals.subtotal)}</span>
          </div>

          {/* VAT Breakdown */}
          {Object.entries(totals.vatBreakdown || {}).map(([rate, values]) => (
            <div key={rate} className="flex justify-between text-sm text-gray-500 mb-1">
              <span>{vatLabel} {rate}</span>
              <span className="font-mono">{formatPrice(values.vat)}</span>
            </div>
          ))}

          <Separator className="my-3" />

          {/* Total */}
          <div className="flex justify-between text-xl font-bold text-gray-800">
            <span>Total</span>
            <span className="font-mono">{formatPrice(totals.total)}</span>
          </div>

          {/* Net/Gross Display */}
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>Netto: {formatPrice(totals.subtotal)}</span>
            <span>Brutto: {formatPrice(totals.total)}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="p-3 border-t space-y-2">
        {/* Primary Actions */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            data-testid="btn-hold-order"
            variant="outline"
            className="h-12 gap-2"
            onClick={() => setShowHoldDialog(true)}
            disabled={order.items.length === 0}
          >
            <Pause className="w-5 h-5" />
            Hold
          </Button>
          <Button
            data-testid="btn-payment"
            className="h-12 gap-2 bg-brand-orange hover:bg-brand-orange/90"
            onClick={onPayment}
            disabled={order.items.length === 0}
          >
            <CreditCard className="w-5 h-5" />
            Pay
          </Button>
        </div>

        {/* Secondary Actions */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            data-testid="btn-print-bill"
            variant="outline"
            className="h-10 gap-2 text-sm"
            disabled={order.items.length === 0}
          >
            <Receipt className="w-4 h-4" />
            Print Bill
          </Button>
          <Button
            data-testid="btn-cancel-order"
            variant="outline"
            className="h-10 gap-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={onCancelOrder}
          >
            <X className="w-4 h-4" />
            Cancel
          </Button>
        </div>
      </div>

      {/* Hold Order Dialog */}
      <Dialog open={showHoldDialog} onOpenChange={setShowHoldDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hold Order</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Note (optional)
            </label>
            <Textarea
              placeholder="e.g., Waiting for customer, Kitchen delay..."
              value={holdNote}
              onChange={(e) => setHoldNote(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHoldDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleHold} className="bg-brand-orange hover:bg-brand-orange/90">
              Hold Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
